import { useMemo } from "react"
import { useNewSubscriptionStepper } from "@common/hooks/subscription/useNewSubscriptionStepper"
import {
    getDiscountAmount,
    getDiscountDeadline,
    getOfferPrice,
    getOfferPricing,
} from "../newSubscriptionUtils"

export const useSubscriptionPricing = ({
    pricingTable,
    sessionsPerWeek,
    plan,
    user,
    amountToPay,
    isDiscountApplied,
    subscriptionMetadata,
}) => {
    const { calculateFirstMonthHours } = useNewSubscriptionStepper()

    const offerPricing = useMemo(() => {
        if (!plan) return null
        return getOfferPricing({
            pricingTable,
            offerType: plan.offerType,
            sessionsPerWeek,
            withCommitment: plan.withCommitment,
        })
    }, [plan])

    const { hoursPerMonth, monthlyPrice, hourlyPrice } = useMemo(() => {
        if (!plan || !offerPricing)
            return { hoursPerMonth: 0, monthlyPrice: 0, hourlyPrice: 0 }
        const pricing = getOfferPrice({
            offerPricing,
            hoursPerWeek: plan.hoursPerWeek,
        })

        // When admin set a custom ttc_price, use it instead of the pricing table
        if (subscriptionMetadata?.ttc_price != null) {
            const customHourly = subscriptionMetadata.ttc_price
            return {
                ...pricing,
                hourlyPrice: customHourly,
                monthlyPrice:
                    Math.round(customHourly * pricing.hoursPerMonth * 10) / 10,
            }
        }

        return pricing
    }, [plan, offerPricing, subscriptionMetadata?.ttc_price])

    const firstMonthHours = useMemo(
        () =>
            plan ? calculateFirstMonthHours(plan.startDate, hoursPerMonth) : 0,
        [plan?.startDate, hoursPerMonth]
    )

    const noDiscountPrice = useMemo(
        () => firstMonthHours * hourlyPrice,
        [firstMonthHours, hourlyPrice]
    )

    const discountDeadline = useMemo(
        () =>
            plan
                ? getDiscountDeadline({
                      user,
                      startDate: plan.startDate,
                  })
                : null,
        [plan?.startDate, user]
    )
    const discountAmount = getDiscountAmount({
        amountToPay,
        isDiscountApplied,
        noDiscountPrice,
    })

    return {
        offerPricing,
        hoursPerMonth,
        monthlyPrice,
        hourlyPrice,
        firstMonthHours,
        noDiscountPrice,
        discountDeadline,
        discountAmount,
    }
}
